import { type Readable, readable, readonly, writable } from 'svelte/store';
import { match, P } from 'ts-pattern';

type ScreenSizeKey = 'ms' | 'mm' | 'ml' | 't' | 'ls' | 'lm' | 'll';
type ScreenSize = { minValue: number; maxValue?: number };

export const screenSizes: Record<ScreenSizeKey, ScreenSize> = {
    ms: { minValue: 320, maxValue: 374 }, // mobile small
    mm: { minValue: 375, maxValue: 424 }, // mobile medium
    ml: { minValue: 425, maxValue: 767 }, // mobile large
    t: { minValue: 768, maxValue: 1023 }, // tablet
    ls: { minValue: 1024, maxValue: 1099 }, // laptop small
    lm: { minValue: 1100, maxValue: 1439 }, // laptop medium
    ll: { minValue: 1440 }, // laptop large
};

export const containerSizes = {
    ls: 1024,
    lm: 1100,
    ll: 1200,
} as const;

type SizeKey = keyof typeof screenSizes;

export type Query =
    | {
          only: SizeKey;
      }
    | {
          gt: SizeKey;
      }
    | {
          gte: SizeKey;
      }
    | {
          lt: SizeKey;
      }
    | {
          lte: SizeKey;
      };

export function queryToMediaQuery(query: Query): string {
    return match(query)
        .with({ only: P.select() }, (sizeKey) => {
            const size = screenSizes[sizeKey];
            const min = `(min-width: ${size.minValue}px)`;
            if (size.maxValue == null) {
                return min;
            }
            return `${min} and (max-width: ${size.maxValue}px)`;
        })
        .with({ lt: P.select() }, (sizeKey) => {
            const size = screenSizes[sizeKey];
            return `(max-width: ${size.minValue - 1}px)`;
        })
        .with({ lte: P.select() }, (sizeKey) => {
            const size = screenSizes[sizeKey];
            if (size.maxValue == null) {
                return `(max-width: 100000000px)`;
            }
            return `(max-width: ${size.maxValue}px)`;
        })
        .with({ gt: P.select() }, (sizeKey) => {
            const size = screenSizes[sizeKey];
            if (size.maxValue == null) {
                return `(min-width: 100000000px)`;
            }
            return `(min-width: ${size.maxValue + 1}px)`;
        })
        .with({ gte: P.select() }, (sizeKey) => {
            const size = screenSizes[sizeKey];
            return `(min-width: ${size.minValue}px)`;
        })
        .exhaustive();
}

export function mediaQuery(query: string | Query, def: boolean): Readable<boolean> {
    const isSupported = typeof window != 'undefined' && 'matchMedia' in window;
    if (!isSupported) {
        return readable(def);
    }

    const mql = window.matchMedia(
        typeof query == 'string' ? query : queryToMediaQuery(query),
    );

    return readonly(
        writable(mql.matches, (set, _update) => {
            const mqlListener = () => set(mql.matches);

            if ('addEventListener' in mql) {
                mql.addEventListener('change', mqlListener);
            } else {
                const m = mql as MediaQueryList;
                m.addListener(mqlListener);
            }

            return () => {
                if ('removeEventListener' in mql) {
                    mql.removeEventListener('change', mqlListener);
                } else {
                    const m = mql as MediaQueryList;
                    m.removeListener(mqlListener);
                }
            };
        }),
    );
}
